import { useState } from 'react';
import { useCan, useOrganizationId, useSubjectLanguageVariants } from '@features/user';
import { Alert, Box, Button, Collapse, Skeleton } from '@mui/material';

import { useGetBatchPreviewQuery, useGetRequestCopyPreviewQuery } from '../store/api';
import { toBulkLaunchPreviewData } from '../store/bulkLaunchMapping';

import { BulkLaunchPreview } from './BulkLaunchPreview';

export interface BulkLaunchRowProps {
  definitionId: string;
  requirementLabel: string;
  requirementKind?: string;
}

/**
 * Bulk launch from a requirement's catalog row (AC-10 … AC-14).
 *
 * **The preview is practice-admin only, and that is not a display rule.**
 * Verifying the population settles requirement rows org-wide and attributes
 * the read to the caller, so it is a write wearing a GET. A non-admin must
 * never CALL it — hiding the result afterwards would still have mutated state
 * on their behalf — which is why the query is skipped rather than the panel
 * hidden, and why the control that opens it is withheld rather than disabled.
 *
 * Launch, progress and cancel are not here: cut 2's endpoints do not exist
 * yet, and a control that cannot do its job is worse than an absent one.
 */
export const BulkLaunchRow = ({
  definitionId,
  requirementLabel,
  requirementKind,
}: BulkLaunchRowProps) => {
  const isAdmin = useCan('manage_organization');
  const organizationId = useOrganizationId();
  const subject = useSubjectLanguageVariants();
  const [open, setOpen] = useState(false);

  // Not merely hidden: skipped. See the note above.
  const canPreview = isAdmin && Boolean(organizationId);

  const {
    data: preview,
    isFetching,
    isError: previewFailed,
    refetch: refetchPreview,
  } = useGetBatchPreviewQuery(
    { orgId: organizationId ?? '', definitionId },
    {
      skip: !canPreview || !open,
      /**
       * Every open asks the server again, and that is not about freshness.
       *
       * The preview VERIFIES the population as it reads it — that is the AC-10
       * side effect, and it is attributed to the caller. Serving a reopen from
       * RTK Query's retained cache would show an audience that was verified
       * minutes ago and never re-verify it, so the admin would be reviewing a
       * claim the server is no longer making.
       */
      refetchOnMountOrArgChange: true,
    },
  );

  /**
   * The message is org-wide copy, not part of the batch preview — the audience
   * and the message are two different questions, answered by two endpoints.
   * Both are needed before a launch can be offered: AC-11 refuses to send
   * behind a message nobody has read.
   */
  const {
    data: copy,
    isFetching: copyIsFetching,
    isError: copyFailed,
    refetch: refetchCopy,
  } = useGetRequestCopyPreviewQuery(
    { orgId: organizationId ?? '' },
    { skip: !canPreview || !open },
  );

  if (!isAdmin) return null;

  /**
   * The two failures are not the same failure, and collapsing them would lose
   * the more useful half.
   *
   * No audience means there is nothing to render at all — the panel is a
   * description of a population, so it needs a retry rather than a shape.
   * No COPY, with the audience in hand, is precisely what the panel's own
   * "the message could not be loaded" alert says, and it already withholds the
   * send behind it. Replacing that with a generic error would tell an admin
   * less than the panel would have.
   */
  const audienceFailed = previewFailed;

  /**
   * The two requests do not finish together, and an absent message reads on
   * the panel as a load failure. Rendering the moment the audience lands would
   * therefore show "the message could not be loaded" for the ordinary case of
   * one request being quicker than the other — so wait for copy, unless copy
   * is the thing that failed, in which case that alert is now true.
   */
  const stillLoading =
    isFetching || copyIsFetching || (Boolean(preview) && !copy && !copyFailed);

  const data = preview
    ? toBulkLaunchPreviewData(
        preview,
        copy
          ? {
              smsBody: copy.sms.body,
              emailSubject: copy.email.subject,
              emailBody: copy.email.body,
            }
          : undefined,
      )
    : undefined;

  return (
    <Box>
      {!open && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => setOpen(true)}
          sx={{ textTransform: 'none' }}
        >
          Send requests…
        </Button>
      )}

      <Collapse in={open} unmountOnExit>
        {audienceFailed ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  void refetchPreview();
                  if (copyFailed) void refetchCopy();
                }}
              >
                Try again
              </Button>
            }
          >
            The preview could not be loaded, so there is no audience to review.
          </Alert>
        ) : stillLoading ? (
          <Skeleton variant="rounded" height={220} />
        ) : data ? (
          <BulkLaunchPreview
            requirementLabel={requirementLabel}
            requirementKind={requirementKind}
            eligiblePatients={data.eligiblePatients}
            alreadyCompletePatients={data.alreadyCompletePatients}
            excludedPatients={data.excludedPatients}
            messagesToSend={data.messagesToSend}
            exclusionBreakdown={data.exclusionBreakdown}
            excludedPatientsPage={data.excludedPatientsPage}
            messagePreview={data.messagePreview}
            canLaunch={data.canLaunch}
            subject={subject}
            onDismiss={() => setOpen(false)}
          />
        ) : null}
      </Collapse>
    </Box>
  );
};
